export interface FunctionalIterator<T, TReturn = any, TNext = undefined> extends Iterator<T, TReturn, TNext> {
  map<U>(f: (t: T, i: number) => U): FunctionalIterator<U>;
  flatMap<U>(f: (t: T, i: number) => Iterable<U>): FunctionalIterator<U>;
  filter(p: (t: T) => boolean): FunctionalIterator<T>;
  takeWhile(p: (t: T) => boolean): FunctionalIterator<T>;
  dropWhile(p: (t: T) => boolean): FunctionalIterator<T>;
  take(n: number): FunctionalIterator<T>;
  drop(n: number): FunctionalIterator<T>;
  find(p: (t: T) => boolean): T | undefined;
  head(): T | undefined;
  tail(): FunctionalIterator<T>;
  prepend(other: Iterable<T>): FunctionalIterator<T>;
  concat(other: Iterable<T>): FunctionalIterator<T>;
  memoize(): FunctionalIterator<T>;
  collect(): T[];
}

// eslint-disable-next-line import/prefer-default-export
export function functional<T>(ts: Iterable<T> | Iterator<T>): FunctionalIterator<T> {
  if (Symbol.iterator in Object(ts)) {
    return wrap(ts[Symbol.iterator]());
  }
  return wrap(ts as Iterator<T>);
}

export function traverse<T>(t: T, f: (t: T) => T | undefined): FunctionalIterator<T> {
  return wrap(generate(t, f));
}

const wrap = <T>(ts: Iterator<T>): FunctionalIterator<T> => ({
  ...ts,
  map: <U>(f: (t: T, i: number) => U) => wrap(map(ts, f)),
  flatMap: <U>(f: (t: T, i: number) => Iterable<U>) => wrap(flatMap(ts, f)),
  filter: (p: (t: T) => boolean) => wrap(filter(ts, p)),
  takeWhile: (p: (t: T) => boolean) => wrap(takeWhile(ts, p)),
  dropWhile: (p: (t: T) => boolean) => wrap(dropWhile(ts, p)),
  find: (p: (t: T) => boolean) => wrap(filter(ts, p)).head(),
  take: (n: number) => wrap(takeWhile(ts, (__, i) => i < n)),
  drop: (n: number) => wrap(dropWhile(ts, (__, i) => i < n)),
  head: () => [...takeWhile(ts, (__, i) => i === 0)][0],
  tail: () => wrap(dropWhile(ts, (__, i) => i === 0)),
  prepend: (other: Iterable<T>) => wrap(concat(other[Symbol.iterator](), ts)),
  concat: (other: Iterable<T>) => wrap(concat(ts, other[Symbol.iterator]())),
  memoize: () => functional(collect(ts)),
  collect: () => collect(ts),
});

function* generate<T>(t: T, f: (t: T) => T | undefined): Generator<T, void, unknown> {
  let v: T | undefined = t;
  do {
    v = f(v);
    if (v !== undefined) {
      yield v;
    }
  } while (v !== undefined);
}

function* map<T, U>(ts: Iterator<T>, f: (t: T, i: number) => U) {
  let i = 0;
  let v = ts.next();
  while (!v.done) {
    yield f(v.value, i);
    v = ts.next();
    i++;
  }
}

function* flatMap<T, U>(ts: Iterator<T>, f: (t: T, i: number) => Iterable<U>) {
  let i = 0;
  let v = ts.next();
  while (!v.done) {
    for (const t of f(v.value, i)) {
      yield t;
    }
    v = ts.next();
    i++;
  }
}

function* filter<T>(ts: Iterator<T>, p: (t: T) => boolean) {
  let v = ts.next();
  while (!v.done) {
    if (p(v.value)) {
      yield v.value;
    }
    v = ts.next();
  }
}

function* takeWhile<T>(ts: Iterator<T>, p: (t: T, i: number) => boolean) {
  let i = 0;
  let v = ts.next();
  while (!v.done) {
    if (p(v.value, i)) {
      yield v.value;
      v = ts.next();
      i++;
    } else {
      return;
    }
  }
}

function* dropWhile<T>(ts: Iterator<T>, p: (t: T, i: number) => boolean) {
  let i = 0;
  let done = false;
  let v = ts.next();
  while (!v.done) {
    if (!done) {
      done = !p(v.value, i);
    }
    if (done) {
      yield v.value;
    }
    v = ts.next();
    i++;
  }
}

function* concat<T>(first: Iterator<T>, second: Iterator<T>) {
  let v = first.next();
  while (!v.done) {
    yield v.value;
    v = first.next();
  }
  v = second.next();
  while (!v.done) {
    yield v.value;
    v = second.next();
  }
}

function collect<T>(ts: Iterator<T>) {
  const result: T[] = [];
  let v = ts.next();
  while (!v.done) {
    result.push(v.value);
    v = ts.next();
  }
  return result;
}
